// src/components/Auth/ResetPasswordPage.js

import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import styles from './ResetPasswordPage.module.css';
// --- NEW IMPORTS ---
import PasswordStrengthIndicator from './PasswordStrengthIndicator'; // Import the indicator
import { getPasswordValidationState } from '../../utils/validation'; // Import the validation utility
// --- END NEW IMPORTS ---

const ResetPasswordPage = () => {
  // (Keep existing state: email, newPassword, confirmNewPassword, error, success, loading)
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resetFinished, setResetFinished] = useState(false);
  const [loading, setLoading] = useState(false);

  // (Keep existing state for URL handling: location, navigate, oobCode, mode, actionEmail)
  const location = useLocation();
  const navigate = useNavigate();
  const [oobCode, setOobCode] = useState(null);
  const [mode, setMode] = useState(null);
  const [actionEmail, setActionEmail] = useState(null);
  const [actionProcessed, setActionProcessed] = useState(false); // <-- New state to track processing

  // --- NEW STATE for Password Validation ---
  const [newPasswordValidation, setNewPasswordValidation] = useState(null);
  const [showNewPasswordRequirements, setShowNewPasswordRequirements] =
    useState(false);
  // --- END NEW STATE ---

  // (Keep existing context access: resetPassword, confirmResetPassword, verifyResetCode, applyActionCode, logout)
  const {
    resetPassword,
    confirmResetPassword,
    verifyResetCode,
    applyActionCode,
    logout,
    currentUser,
  } = useAuth();

  console.log('[ResetPasswordPage] Render State:', {
    mode,
    oobCode: !!oobCode,
    actionEmail,
    loading,
    success: !!success,
  });

  // (Keep existing useEffect for checking URL parameters)
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const oobCodeParam = queryParams.get('oobCode');
    const modeParam = queryParams.get('mode');

    console.log(
      `[ResetPasswordPage - useEffect] Checking URL: mode=${modeParam}, oobCode=${oobCodeParam ? '...' : 'null'}`,
    );

    // --- FIX: Only enter this block if parameters exist AND we haven't processed them yet ---
    if (oobCodeParam && modeParam && !actionProcessed) {
      setOobCode(oobCodeParam);
      setMode(modeParam);
      setError('');
      setSuccess('');
      setLoading(true);
      setActionProcessed(true); // This prevents the infinite loop

      if (modeParam === 'resetPassword') {
        verifyResetCode(oobCodeParam)
          .then((info) => {
            console.log(
              '[ResetPasswordPage - verifyCode] Code verified successfully:',
              info,
            );
            const emailValue = typeof info === 'string' ? info : info?.email;
            console.log(
              '[ResetPasswordPage] Setting actionEmail to:',
              emailValue,
            );

            setActionEmail(emailValue);
            //setSuccess(
            //  'Code verified. Please enter and confirm your new password.',
            //);
            setError('');
            setLoading(false);
            setResetFinished(false);
            // Reset password fields when code is verified
            setNewPassword('');
            setConfirmNewPassword('');
            setNewPasswordValidation(null);
            setShowNewPasswordRequirements(false);
          })
          .catch((error) => {
            console.error(
              '[ResetPasswordPage - verifyCode] Error verifying code:',
              error,
            );
            let errorMessage = 'Invalid or expired reset code.';
            // --- ALL YOUR ORIGINAL SWITCH LOGIC KEPT BELOW ---
            switch (error.code) {
              case 'auth/invalid-action-code':
                errorMessage = 'The reset code is invalid or has expired.';
                break;
              case 'auth/expired-action-code':
                errorMessage = 'The reset code has expired.';
                break;
              case 'auth/user-disabled':
                errorMessage = 'The user account has been disabled.';
                break;
              case 'auth/user-not-found':
                errorMessage = 'User account not found.';
                break;
              case 'auth/network-request-failed':
                errorMessage =
                  'Your network connection was lost. Please check your internet connection and try again.';
                break;
              default:
                errorMessage = `Error verifying code: ${error.message || 'An unexpected error occurred.'}`;
            }
            setError(errorMessage);
            //setSuccess('');
            setLoading(false);
            // We clear these on ERROR so the user is returned to the "Send Email" form
            setOobCode(null);
            setMode(null);
            setActionEmail(null);
            setActionProcessed(false);
          });
      } else if (modeParam === 'verifyEmail') {
        applyActionCode(oobCodeParam)
          .then(async () => {
            console.log(
              '[ResetPasswordPage - applyCode] Email verified successfully via oobCode.',
            );
            setSuccess(
              'Your email has been successfully verified! Redirecting...',
            );
            setError('');
            setLoading(false);

            // --- YOUR ORIGINAL RELOAD LOGIC ---
            if (currentUser) {
              try {
                await currentUser.reload();
                await currentUser.getIdToken(true); // <--- ADD THIS: Forces AuthContext to update globally!
                console.log(
                  '[ResetPasswordPage - applyCode] currentUser refreshed and React state updated.',
                );
              } catch (reloadError) {
                console.warn(
                  '[ResetPasswordPage - applyCode] Error reloading user:',
                  reloadError,
                );
              }
            }

            // --- YOUR ORIGINAL NAVIGATION LOGIC ---
            setTimeout(() => {
              window.location.href = '/';
            }, 3000);
          })
          .catch((error) => {
            console.error(
              '[ResetPasswordPage - applyCode] Error applying action code:',
              error,
            );
            let errorMessage = 'Invalid or expired verification code.';
            // --- ALL YOUR ORIGINAL SWITCH LOGIC KEPT BELOW ---
            switch (error.code) {
              case 'auth/invalid-action-code':
                errorMessage =
                  'The verification code is invalid or has expired.';
                break;
              case 'auth/expired-action-code':
                errorMessage = 'The verification code has expired.';
                break;
              case 'auth/operation-not-allowed':
                errorMessage = 'Email/password accounts are not enabled.';
                break;
              case 'auth/user-disabled':
                errorMessage = 'The user account has been disabled.';
                break;
              case 'auth/user-not-found':
                errorMessage = 'User account not found.';
                break;
              default:
                errorMessage = `Error verifying email: ${error.message || 'An unexpected error occurred.'}`;
            }
            setError(errorMessage);
            setSuccess('');
            setLoading(false);
            setOobCode(null);
            setMode(null);
            setActionEmail(null);
            setActionProcessed(false);
          });
      } else {
        console.warn(
          `[ResetPasswordPage - useEffect] Unknown mode '${modeParam}' in URL.`,
        );
        setError('Invalid action mode in URL.');
        setSuccess('');
        setLoading(false);
        setOobCode(null);
        setMode(null);
        setActionEmail(null);
        setActionProcessed(false);
      }
    }
    // --- FIX: The "Else" now only triggers if the URL actually lacks the parameters ---
    else if (!oobCodeParam || !modeParam) {
      // Only clear the form if we aren't currently in the middle of a successful verification
      if (!actionProcessed) {
        console.log(
          '[ResetPasswordPage - useEffect] No action code found. Displaying email request form.',
        );
        setOobCode(null);
        setMode(null);
        setActionEmail(null);
        setLoading(false);
        setActionProcessed(false);
      }
    }
  }, [
    location.search,
    navigate,
    verifyResetCode,
    applyActionCode,
    actionProcessed,
    currentUser,
  ]);

  // --- NEW Effect: Validate new password as it changes (only when in reset mode) ---
  useEffect(() => {
    // Only run validation if we are in password reset mode and have a code
    if (mode === 'resetPassword' && oobCode) {
      if (newPassword) {
        const validation = getPasswordValidationState(newPassword);
        setNewPasswordValidation(validation);
        // Keep requirements visible while typing
        setShowNewPasswordRequirements(true);
      } else {
        setNewPasswordValidation(null); // Clear validation if empty
        // Hide requirements if field becomes empty (blur handler also does this)
        setShowNewPasswordRequirements(false);
      }
    } else {
      // If not in reset mode, ensure validation state is cleared
      setNewPasswordValidation(null);
      setShowNewPasswordRequirements(false);
    }
  }, [newPassword, mode, oobCode]); // Depend on password, mode, and code
  // --- END NEW Effect ---

  // (Keep existing handleSendResetEmail handler - no changes needed)
  const handleSendResetEmail = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      await resetPassword(email);
      setSuccess('Password reset email sent! Please check your inbox.');
      setEmail('');
      console.log(`[ResetPasswordPage] Sent password reset email to: ${email}`);
    } catch (err) {
      console.error('[ResetPasswordPage] Password reset error:', err);
      let errorMessage = 'Failed to send password reset email.';
      switch (err.code) {
        case 'auth/user-not-found':
          errorMessage = 'No user found with this email address.';
          break;
        case 'auth/invalid-email':
          errorMessage = 'The email address is invalid.';
          break;
        default:
          errorMessage = `Failed to send reset email: ${err.message || 'An unexpected error occurred.'}`;
          break;
      }
      setError(errorMessage);
      setSuccess('');
    } finally {
      setLoading(false);
    }
  };

  // --- NEW Handlers for New Password Focus/Blur ---
  const handleNewPasswordFocus = () => {
    // Show requirements only when in the correct mode
    if (mode === 'resetPassword' && oobCode) {
      setShowNewPasswordRequirements(true);
    }
  };
  const handleNewPasswordBlur = () => {
    // Hide requirements on blur ONLY if the field is empty
    if (!newPassword) {
      setShowNewPasswordRequirements(false);
    }
  };
  // --- END NEW Handlers ---

  // --- UPDATE handleConfirmPasswordReset handler ---
  const handleConfirmPasswordReset = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(''); // Clear previous success message before attempting reset

    // --- Frontend Validations ---
    if (!oobCode) {
      setError('Missing reset code. Please use the link from your email.');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setError('New passwords do not match.');
      // Keep indicator visible if passwords don't match but strength was checked
      setShowNewPasswordRequirements(true);
      return;
    }

    // NEW: Check password strength using the utility function
    const validation = getPasswordValidationState(newPassword);
    setNewPasswordValidation(validation); // Update state for indicator
    setShowNewPasswordRequirements(true); // Ensure indicator is visible if submit fails

    if (!validation.isValid) {
      setError(
        'New password does not meet all requirements. Please check the list below.',
      );
      return; // Stop if requirements not met
    }
    // --- End Frontend Validations ---

    setLoading(true); // Start loading *after* frontend checks pass
    try {
      await confirmResetPassword(oobCode, newPassword);

      setSuccess(
        'Your password has been reset successfully! Redirecting to login...',
      );
      setResetFinished(true);
      setNewPassword('');
      setConfirmNewPassword('');
      setNewPasswordValidation(null); // Clear validation state on success
      setShowNewPasswordRequirements(false); // Hide indicator on success
      console.log(
        `[ResetPasswordPage] Password successfully reset for ${actionEmail || 'user'}.`,
      );

      // Attempt logout *before* redirecting to ensure clean state
      try {
        await logout();
      } catch (logoutError) {
        console.warn('Logout after password reset failed:', logoutError);
      }

      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 3000);
    } catch (err) {
      console.error(
        '[ResetPasswordPage - confirmPasswordReset] Error confirming password reset:',
        err,
      );
      let errorMessage = 'Failed to reset password.';
      switch (err.code) {
        case 'auth/expired-action-code':
        case 'auth/invalid-action-code':
          errorMessage =
            'The reset code is invalid or has expired. Please request a new one.';
          setOobCode(null);
          setMode(null);
          setActionEmail(null); // Reset view
          break;
        case 'auth/user-disabled':
          errorMessage = 'The user account has been disabled.';
          break;
        case 'auth/user-not-found':
          errorMessage = 'User account not found.';
          break;
        case 'auth/weak-password': // Should be caught by our check, but keep as fallback
          errorMessage = 'Password is too weak (rejected by server).';
          break;
        default:
          errorMessage = `Failed to reset password: ${err.message || 'An unexpected error occurred.'}`;
      }
      setError(errorMessage);
      setSuccess(''); // Clear success message on error
      // Keep indicator visible on error if it was already shown
      setShowNewPasswordRequirements(true);
    } finally {
      setLoading(false);
    }
  };

  // --- Render Logic ---

  // Mode: 'resetPassword' - Confirmation Form
  if (mode === 'resetPassword' && oobCode) {
    return (
      <div className={styles['reset-password-page']}>
        <div className={styles['reset-form-container']}>
          <h2 className={styles['reset-heading']}>Set New Password</h2>

          {/* Loading indicator during code verification */}
          {loading && !actionEmail && !error && (
            <div className={styles['message-area']}>
              <p>Verifying code...</p>
            </div>
          )}

          {/* Display messages */}
          {error && (
            <div
              className={`${styles['message-area']} ${styles['error-message']}`}
            >
              {error}
            </div>
          )}
          {/* Show success message only if not actively loading the reset itself */}
          {(success || (actionEmail && !loading)) && (
            <div
              className={`${styles['message-area']} ${styles['success-message']}`}
            >
              {success}
            </div>
          )}

          {actionEmail && (
            <p className={styles.actionEmailDisplay}>
              Resetting password for: <strong>{actionEmail}</strong>
            </p>
          )}

          {/* New Password Confirmation Form - Show if code verified (actionEmail set) AND not successfully reset yet */}
          {actionEmail &&
            !success && ( // Show form only if code is verified and reset hasn't succeeded yet
              <form
                onSubmit={handleConfirmPasswordReset}
                className={styles['reset-form']}
                noValidate
              >
                {' '}
                {/* Add noValidate */}
                {/* New Password Input */}
                <div className={styles['form-group']}>
                  <label htmlFor="newPassword">New Password:</label>
                  <input
                    type="password"
                    id="newPassword"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    onFocus={handleNewPasswordFocus} // <-- ADD Focus Handler
                    onBlur={handleNewPasswordBlur} // <-- ADD Blur Handler
                    required
                    disabled={loading} // Disable during reset attempt
                    autoComplete="new-password"
                    // Add aria-describedby for accessibility
                    aria-describedby={
                      showNewPasswordRequirements
                        ? 'reset-password-reqs'
                        : undefined
                    }
                  />
                  {/* --- RENDER Password Strength Indicator --- */}
                  {showNewPasswordRequirements && (
                    <div id="reset-password-reqs">
                      {' '}
                      {/* Container with ID */}
                      <PasswordStrengthIndicator
                        validationState={newPasswordValidation}
                      />
                    </div>
                  )}
                  {/* --- END RENDER --- */}
                </div>
                {/* Confirm New Password Input */}
                <div className={styles['form-group']}>
                  <label htmlFor="confirmNewPassword">
                    Confirm New Password:
                  </label>
                  <input
                    type="password"
                    id="confirmNewPassword"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    required
                    disabled={loading} // Disable during reset attempt
                    autoComplete="new-password"
                  />
                </div>
                {/* Submit Button */}
                <button
                  type="submit"
                  className={styles['submit-button']}
                  disabled={loading}
                >
                  {loading ? 'Resetting...' : 'Set New Password'}
                </button>
              </form>
            )}

          {/* Link back to Login Page */}
          {/* Show link always, except maybe during the final loading state after success? */}
          {!loading && (
            <Link to="/login" className={styles['back-to-login']}>
              Back to Login
            </Link>
          )}
          {/* Or show always: */}
          {/* <Link to="/login" className={styles['back-to-login']}>Back to Login</Link> */}
        </div>
      </div>
    );
  }
  // Mode: 'verifyEmail' - Verification Message
  else if (mode === 'verifyEmail' && oobCode) {
    return (
      /* ... existing verifyEmail JSX ... */
      <div className={styles['reset-password-page']}>
        <div className={styles['reset-form-container']}>
          <h2 className={styles['reset-heading']}>
            Verifying Email Address...
          </h2>
          {loading && !error && !success && (
            <div className={styles['message-area']}>
              <p>Processing verification code...</p>
            </div>
          )}
          {error && (
            <div
              className={`${styles['message-area']} ${styles['error-message']}`}
            >
              {error}
            </div>
          )}
          {success && (
            <div
              className={`${styles['message-area']} ${styles['success-message']}`}
            >
              {success}
            </div>
          )}
          {!loading && success && (
            <div className={styles['message-area']}>
              <p>Your email has been successfully verified!</p>
            </div>
          )}
          {!loading && error && (
            <div className={styles['message-area']}>
              <p>Please check the link or try requesting a new email.</p>
            </div>
          )}
          {!loading && (
            <Link to="/login" className={styles['back-to-login']}>
              Back to Login
            </Link>
          )}
        </div>
      </div>
    );
  }
  // Default: Email Request Form
  else {
    return (
      /* ... existing email request form JSX ... */
      <div className={styles['reset-password-page']}>
        <div className={styles['reset-form-container']}>
          <h2 className={styles['reset-heading']}>Reset Password</h2>
          <div className={styles['message-area']}>
            {error && <p className={styles['error-message']}>{error}</p>}
            {success && <p className={styles['success-message']}>{success}</p>}
            {!error && !success && (
              <p>
                {' '}
                Enter your recovery email address below to receive a password
                reset link.{' '}
              </p>
            )}
          </div>
          <form
            onSubmit={handleSendResetEmail}
            className={styles['reset-form']}
          >
            <div className={styles['form-group']}>
              <label htmlFor="email">Email Address:</label>
              <input
                type="email"
                id="email"
                value={email}
                placeholder="Enter your recovery email address"
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                autoComplete="email"
              />
            </div>
            <button
              type="submit"
              className={styles['submit-button']}
              disabled={loading}
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </form>
          <Link to="/login" className={styles['back-to-login']}>
            {' '}
            Back to Login{' '}
          </Link>
        </div>
      </div>
    );
  }
};

export default ResetPasswordPage;
